import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";

export default function AddProject() {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const [projects, setProjects] = useState([]);
  const [editingId, setEditingId] = useState(null);

  // ✅ تحميل المشاريع من Firestore
  const fetchProjects = async () => {
    const querySnapshot = await getDocs(collection(db, "projects"));
    const projectList = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setProjects(projectList.sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds));
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // ✅ رفع الصورة لـ Cloudinary
  const uploadImageToCloudinary = async (file) => {
    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", "iisdweb");
    data.append("cloud_name", "dys8jv8c2");

    const res = await fetch(`https://api.cloudinary.com/v1_1/dys8jv8c2/upload`, {
      method: "POST",
      body: data,
    });

    const uploaded = await res.json();
    return uploaded.secure_url;
  };

  // ✅ إضافة أو تعديل مشروع
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setUploading(true);

    try {
      let imageUrl = "";

      // لو في صورة جديدة
      if (image) {
        imageUrl = await uploadImageToCloudinary(image);
      }

      if (editingId) {
        // تعديل مشروع
        const projectRef = doc(db, "projects", editingId);
        await updateDoc(projectRef, {
          title,
          description,
          ...(imageUrl && { imageUrl }),
        });
        setEditingId(null);
        setSuccess(true);
      } else {
        // إضافة مشروع جديد
        await addDoc(collection(db, "projects"), {
          title,
          description,
          imageUrl,
          createdAt: serverTimestamp(),
        });
        setSuccess(true);
      }

      setTitle("");
      setDescription("");
      setImage(null);
      fetchProjects();
    } catch (err) {
      console.error(err);
      setError("حدث خطأ أثناء حفظ المشروع");
    } finally {
      setUploading(false);
    }
  };

  // ✅ حذف مشروع
  const handleDelete = async (id) => {
    if (window.confirm("هل أنت متأكد من حذف هذا المشروع؟")) {
      await deleteDoc(doc(db, "projects", id));
      fetchProjects();
    }
  };

  // ✅ بدء التعديل
  const handleEdit = (project) => {
    setEditingId(project.id);
    setTitle(project.title);
    setDescription(project.description);
    setImage(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>
        {editingId ? "تعديل مشروع" : "إضافة مشروع جديد"}
      </h2>

      {/* نموذج الإضافة / التعديل */}
      <form onSubmit={handleSubmit} style={styles.form}>
        <input
          type="text"
          placeholder="عنوان المشروع"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          style={styles.input}
        />

        <textarea
          placeholder="وصف المشروع"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          style={{ ...styles.input, height: "100px", resize: "none" }}
        />

        <input
          type="file"
          accept="image/*"
          onChange={(e) => setImage(e.target.files[0])}
          style={styles.fileInput}
        />

        <button type="submit" style={styles.button} disabled={uploading}>
          {uploading
            ? "جاري الرفع..."
            : editingId
            ? "تحديث المشروع"
            : "إضافة المشروع"}
        </button>

        {success && <p style={styles.success}>✅ تم الحفظ بنجاح</p>}
        {error && <p style={styles.error}>{error}</p>}
      </form>

      {/* جدول المشاريع */}
      <h3 style={{ marginTop: "40px", color: "#004aad" }}>📋 قائمة المشاريع</h3>

      <table style={styles.table}>
        <thead>
          <tr>
            <th>الصورة</th>
            <th>العنوان</th>
            <th>الوصف</th>
            <th>التحكم</th>
          </tr>
        </thead>
        <tbody>
          {projects.length > 0 ? (
            projects.map((p) => (
              <tr key={p.id}>
                <td>
                  {p.imageUrl ? (
                    <img src={p.imageUrl} alt={p.title} style={styles.thumbnail} />
                  ) : (
                    "—"
                  )}
                </td>
                <td>{p.title}</td>
                <td>{p.description}</td>
                <td>
                  <button style={styles.editBtn} onClick={() => handleEdit(p)}>
                    ✏️ تعديل
                  </button>
                  <button style={styles.deleteBtn} onClick={() => handleDelete(p.id)}>
                    🗑️ حذف
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4" style={{ textAlign: "center", padding: "20px" }}>
                لا توجد مشاريع بعد.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// 🎨 التنسيقات
const styles = {
  container: {
    maxWidth: "900px",
    margin: "40px auto",
    padding: "30px",
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
    textAlign: "center",
    fontFamily: "Cairo, sans-serif",
    direction: "rtl",
  },
  heading: {
    color: "#004aad",
    marginBottom: "25px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },
  input: {
    padding: "12px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "15px",
    outline: "none",
  },
  fileInput: {
    border: "none",
    fontSize: "14px",
  },
  button: {
    padding: "12px",
    background: "#004aad",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: "15px",
  },
  success: {
    color: "green",
    fontWeight: "bold",
    marginTop: "10px",
  },
  error: {
    color: "red",
    fontWeight: "bold",
    marginTop: "10px",
  },
  table: {
    width: "100%",
    marginTop: "20px",
    borderCollapse: "collapse",
  },
  thumbnail: {
    width: "80px",
    height: "60px",
    objectFit: "cover",
    borderRadius: "6px",
  },
  editBtn: {
    background: "#ffc107",
    border: "none",
    padding: "5px 10px",
    borderRadius: "5px",
    cursor: "pointer",
    margin: "0 5px",
  },
  deleteBtn: {
    background: "#dc3545",
    border: "none",
    padding: "5px 10px",
    borderRadius: "5px",
    color: "#fff",
    cursor: "pointer",
    margin: "0 5px",
  },
};
